import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product, StockLot } from '@prody/shared';
import {
  Button, Code, DataTable, EmptyState, Gauge, IconExport, Input, ListPane,
  MoneyText, PageHeader, Stamp, Toolbar, Workbench, type Column,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useProducts, useStock } from '~/platform/data/queries';
import { remainingShelfLife } from '~/platform/domain/presentation';
import './stock.css';

/* ==================================================================
   Stock, by lot.

   Perishable goods cannot be a single number per product. The list is
   lots, ordered by what expires first, because that is the order the
   warehouse must ship them in and the order the manager must sell
   them in.

   The freshness gauge is the point of the screen: it reads remaining
   shelf life as a fraction, not a date, so "this needs to move today"
   is visible without arithmetic.
   ================================================================== */

export default function StockPage() {
  const { t } = useTranslation(['stock', 'common', 'catalog']);
  const { business, format } = useBusiness();
  const [search, setSearch] = useState('');

  const { data: lots = [], isLoading } = useStock(business.id, search ? { search } : undefined);
  const { data: products = [] } = useProducts(business.id);

  const productsById = useMemo(
    () => new Map<string, Product>(products.map((p) => [p.id as string, p])),
    [products],
  );

  const today = format.tradingDay();

  const rows = useMemo(
    () =>
      [...lots].sort((a, b) => (a.bestBefore ?? '9999').localeCompare(b.bestBefore ?? '9999')),
    [lots],
  );

  const expiringSoon = rows.filter((lot) => {
    const life = remainingShelfLife(
      lot.bestBefore,
      productsById.get(lot.productId)?.traits.perishable?.shelfLifeDays,
      today,
    );
    return life !== null && life < 0.3;
  }).length;

  const columns = useMemo<Column<StockLot>[]>(
    () => [
      {
        key: 'product',
        header: t('common:labels.product'),
        width: 'minmax(180px, 2fr)',
        sortable: true,
        sortValue: (l) => l.productName,
        cell: (l) => (
          <span className="pd-stock__name">
            <span className="truncate">{l.productName}</span>
            <Code>{l.sku}</Code>
          </span>
        ),
      },
      {
        key: 'lot',
        header: t('lots', { count: 1 }),
        width: '112px',
        hideBelow: 'md',
        cell: (l) => (l.lotCode ? <Code>{l.lotCode}</Code> : <span className="faint">—</span>),
      },
      {
        key: 'location',
        header: t('common:labels.none'),
        width: '132px',
        hideBelow: 'lg',
        cell: (l) => <span className="t-small dim truncate">{l.location ?? '—'}</span>,
      },
      {
        key: 'remaining',
        header: t('onHand'),
        align: 'num',
        width: '108px',
        sortable: true,
        sortValue: (l) => l.remaining.value,
        cell: (l) => <span className="num">{format.quantity(l.remaining)}</span>,
      },
      {
        key: 'reserved',
        header: t('reserved'),
        align: 'num',
        width: '100px',
        hideBelow: 'sm',
        cell: (l) =>
          l.reserved ? (
            <span className="num dim">{format.quantity(l.reserved)}</span>
          ) : (
            <span className="faint">—</span>
          ),
      },
      {
        key: 'life',
        header: t('nextExpiry'),
        width: '158px',
        sortable: true,
        sortValue: (l) => l.bestBefore ?? '9999',
        cell: (l) => {
          const shelfLifeDays = productsById.get(l.productId)?.traits.perishable?.shelfLifeDays;
          const life = remainingShelfLife(l.bestBefore, shelfLifeDays, today);
          if (life === null) {
            return <span className="faint">—</span>;
          }
          return (
            <span className="pd-stock__life">
              <Gauge
                value={life}
                tone={life < 0.15 ? 'alert' : life < 0.35 ? 'warn' : 'ok'}
                label={t('nextExpiry')}
              />
              <span className="num faint">{format.date(l.bestBefore)}</span>
            </span>
          );
        },
      },
      {
        key: 'value',
        header: `${t('valuation')} (${format.currencySymbol()})`,
        align: 'num',
        width: '114px',
        hideBelow: 'lg',
        cell: (l) =>
          l.unitCost ? (
            <MoneyText
              value={{ minor: Math.round(l.unitCost.minor * l.remaining.value), currency: l.unitCost.currency }}
              bare
            />
          ) : (
            <span className="faint">—</span>
          ),
      },
      {
        key: 'status',
        header: t('common:labels.status'),
        width: '110px',
        cell: (l) => (
          <Stamp
            tone={l.status === 'available' ? 'ok' : l.status === 'quarantine' ? 'warn' : 'alert'}
            weight={l.status === 'available' ? 'outline' : 'solid'}
          >
            {l.status}
          </Stamp>
        ),
      },
    ],
    [t, format, productsById, today],
  );

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={isLoading ? undefined : t('lots', { count: rows.length })}
        actions={
          <>
            <Button variant="quiet" icon={<IconExport />}>
              {t('common:actions.export')}
            </Button>
            <Button variant="default">{t('count')}</Button>
          </>
        }
      />

      <Toolbar
        trailing={
          expiringSoon > 0 ? (
            <Stamp tone="warn" weight="solid">
              {t('expiringSoon', { count: expiringSoon })}
            </Stamp>
          ) : null
        }
      >
        <Input
          type="search"
          placeholder={t('common:actions.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ inlineSize: 260 }}
        />
      </Toolbar>

      <ListPane>
        <DataTable<StockLot>
          rows={rows}
          columns={columns}
          rowKey={(l) => l.id}
          rowMeta={(l) => {
            const life = remainingShelfLife(
              l.bestBefore,
              productsById.get(l.productId)?.traits.perishable?.shelfLifeDays,
              today,
            );
            return {
              handlingClass: productsById.get(l.productId)?.handlingClass ?? 'neutral',
              ...(life !== null && life < 0.15
                ? { tone: 'alert' as const }
                : life !== null && life < 0.35
                  ? { tone: 'warn' as const }
                  : {}),
              struck: l.status === 'written-off' || l.status === 'expired',
            };
          }}
          loading={isLoading}
          caption={t('title')}
          empty={<EmptyState title={t('empty')} body={t('emptyBody')} />}
        />
      </ListPane>
    </Workbench>
  );
}
